"use client";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "./Calendar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { displayValue, getStatusColor } from "../../../utils/Helper";
import { Badge } from "../Badge";
import { useState } from "react";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import DetailLogs from "../../admin/tickets/ticket-detail/AuditLog";


interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  dateRange: { start: Date; end: Date };
  onViewEvent?: (event: CalendarEvent) => void;
}

export function MonthView({
  currentDate,
  events,
  dateRange,
  onViewEvent,
}: MonthViewProps) {
  const [openLog, setOpenLog] = useState(false);
  const [ticketId, setTicketId] = useState<undefined | Number>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventsForSelectedDate, setEventsForSelectedDate] = useState<CalendarEvent[]>([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const rows = [];
  let days = [];
  let day = startDate;
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter((event) => isSameDay(new Date(event.start), day));
  };

  // Handle view more button click
  const handleViewMore = (day: Date, events: CalendarEvent[]) => {
    setSelectedDate(day);
    setEventsForSelectedDate(events);
    setIsSheetOpen(true);
  };

   const handleViewEvent = (event: CalendarEvent) => {
     // Assuming your event has a ticket_id property
     setTicketId(Number(event.id));
     setOpenLog(true);
   };



  // Create calendar rows
  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      const currentDay = day;
      const dayEvents = getEventsForDay(currentDay);
      const isCurrentMonth = isSameMonth(day, monthStart);

      days.push(
        <div
          key={day.toString()}
          className={cn(
            "border-r border-b min-h-[100px] p-1",
            !isCurrentMonth && "bg-gray-50/30",
            // i === 3 && "bg-green-50/30", // Highlight Wednesday like in the screenshot
            "overflow-y-auto"
          )}
        >
          <div className="flex flex-col h-full">
            <div
              className={cn("text-sm mb-1", !isCurrentMonth && "text-gray-400")}
            >
              {format(day, "d")}
              {day.getDate() === 1 && (
                <span className="text-xs text-gray-500 ml-1">
                  {format(day, "MMM")}
                </span>
              )}
            </div>
            <div className="flex-1 space-y-1">
              {dayEvents.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  className={cn(
                    "text-xs p-1 rounded-md border cursor-pointer transition-colors gap-2 w-full",
                    "bg-blue-50 border-blue-200 hover:bg-blue-100"
                  )}
                  // onClick={() => onViewEvent?.(event)}
                  onClick={
                   ()=> handleViewEvent(event)
                  }
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="font-medium truncate">
                          {event.title}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-60 break-words">
                        {event.title}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {/* <div className="font-medium text-black break-all">
                    Ticket Number : {event.ticket_number}
                  </div>
                  {event.user?.username && (
                    <div className="font-medium text-black break-all">
                      ITS : {event.user?.username}
                    </div>
                  )} */}
                  {event.user?.name && (
                    <div className="font-medium text-black break-all">
                      Name : {event.user?.name}
                    </div>
                  )}
                  <div className="font-medium text-black break-all">
                    <Badge
                      className={`${getStatusColor(event.status) || "bg-gray-500"} text-white font-medium text-xs`}
                    >
                      {displayValue(event.status.replace(/_/g, " "))}
                    </Badge>
                  </div>
                </div>
              ))}
              {dayEvents.length > 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 text-xs w-full text-blue-600 hover:text-blue-800"
                  onClick={() => handleViewMore(currentDay, dayEvents)}
                >
                  View {dayEvents.length - 3} more
                </Button>
              )}
            </div>
          </div>
        </div>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <div key={day.toString()} className="grid grid-cols-7">
        {days}
      </div>
    );
    days = [];
  }

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-7 border-b">
        {daysOfWeek.map((day, index) => (
          <div
            key={day}
            className={cn(
              "py-2 text-center text-sm border-r",
              // index === 3 && "bg-green-50/30" // Highlight Wednesday header
            )}
          >
            {day}
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">{rows}</div>

      {/* Events Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedDate
                ? `Events for ${format(selectedDate, "EEEE, MMMM d, yyyy")}`
                : "Events"}
            </SheetTitle>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            {eventsForSelectedDate.map((event) => (
              <Card
                key={event.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                // onClick={() => {
                //   onViewEvent?.(event);
                //   setIsSheetOpen(false);
                // }}
                onClick={() => handleViewEvent(event)}
              >
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">{event.title}</h3>
                  <div className="grid gap-1 text-sm">
                    <div className="font-medium">
                      Ticket Number: {event.ticket_number}
                    </div>
                    {event.user?.username && (
                      <div>ITS: {event.user?.username}</div>
                    )}
                    {event.user?.name && <div>Name: {event.user?.name}</div>}
                    <div className="mt-2">
                      <Badge
                        className={`${getStatusColor(event.status) || "bg-gray-500"} text-white font-medium text-xs`}
                      >
                        {displayValue(event.status.replace(/_/g, " "))}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={ticketId} />
    </div>
  );
}
